import React, { useState, useEffect, Suspense } from "react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Hero from "./components/Hero";
import About from "./components/About";
import Values from "./components/Values";
import HeritageBanner from "./components/HeritageBanner";
import JoinSteps from "./components/JoinSteps";
import Meetings from "./components/Meetings";
import FAQ from "./components/FAQ";
import StoriesTeaser from "./components/StoriesTeaser";
import Contact from "./components/Contact";
import Blog from "./components/Blog";
import Privacy from "./components/Privacy";
import { HOME, BLOG, PRIVACY, HOME_SECTION_IDS } from "./components/constants";

const Gallery = React.lazy(() => import("./components/Gallery"));

const THEME = "midnight";
const HERO_BG = "royal";
const FONTS = "classical";
const SCROLLSPY_OFFSET = 120;
const BACK_BUTTON_STYLE = {
  background: "transparent",
  border: 0,
  color: "var(--gold-deep)",
  fontFamily: "var(--font-mono)",
  fontSize: "0.78rem",
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  cursor: "pointer",
  padding: "12px 0",
};

function BackToHome({ onView }) {
  return (
    <div style={{ background: "var(--paper-2)", padding: "32px 0 0" }}>
      <div className="container">
        <button onClick={() => onView(HOME)} style={BACK_BUTTON_STYLE}>
          ← Back to home
        </button>
      </div>
    </div>
  );
}

function App() {
  const [view, setView] = useState(HOME);
  const [activeSection, setActiveSection] = useState(HOME);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", THEME);
    root.setAttribute("data-hero-bg", HERO_BG);
    root.setAttribute("data-fonts", FONTS);
  }, []);

  useEffect(() => {
    if (view !== HOME) return undefined;
    const onScroll = () => {
      let current = HOME_SECTION_IDS[0];
      for (const id of HOME_SECTION_IDS) {
        const el = document.getElementById(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= SCROLLSPY_OFFSET) current = id;
      }
      setActiveSection(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [view]);

  useEffect(() => {
    if (view !== HOME) window.scrollTo({ top: 0, behavior: "instant" });
  }, [view]);

  return (
    <>
      <Header activeSection={activeSection} view={view} onView={setView} />
      <main id="main">
        {view === HOME && (
          <>
            <Hero />
            <About />
            <Values />
            <HeritageBanner />
            <section className="section section--paper2" style={{ paddingTop: 0 }}>
              <div className="container">
                <div className="split">
                  <div>
                    <p className="eyebrow">Joining us</p>
                    <h2>The path from interest to initiation.</h2>
                    <p className="lede">
                      Membership is open to any man over 18, irrespective of race or
                      religion. You don't have to be an RAF veteran — only to share
                      an interest in aviation.
                    </p>
                    <p>
                      Learn more in the{" "}
                      <a
                        href="https://drive.google.com/file/d/1xE50b5PK-mOv0f_LD4UvsDL95fVOqpZI/view"
                        target="_blank"
                        rel="noreferrer"
                      >
                        introductory pack ↗
                      </a>
                      .
                    </p>
                  </div>
                  <JoinSteps />
                </div>
              </div>
            </section>
            <Meetings />
            <FAQ />
            <StoriesTeaser onView={setView} />
            <Suspense fallback={<div style={{ minHeight: "60vh" }} />}>
              <Gallery />
            </Suspense>
            <Contact />
          </>
        )}
        {view === BLOG && (
          <>
            <BackToHome onView={setView} />
            <Blog />
          </>
        )}
        {view === PRIVACY && (
          <>
            <BackToHome onView={setView} />
            <Privacy />
          </>
        )}
      </main>
      <Footer onView={setView} />
    </>
  );
}

export default App;
